/* eslint-disable no-unused-expressions */
/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, March 2020
 */
import { URL, URLSearch } from './URL';
import { Query } from './types';
import { Headers } from './Headers';
import { Body } from './Body';
import { IRequestOptions } from './Request';

/**
 * Available HTTP methods.
 */
export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'PATCH'  | 'DELETE' | 'CONNECT' | 'OPTIONS' | 'TRACE' | 'HEAD' | 'PATCH' ;

export interface IHttpClient {
    /**
     * Make an HTTP request.
     *
     * @param request The request to make.
     */
    request<T, R>(request: IHttpClientRequest<T>): Promise<IHttpClientResponse<R>>;
}

/**
 * Client options.
 */
export interface IHttpClientOptions {
    /**
     * Base URL to resolve requests with.
     */
    url?: string;
    /**
     * Default query parameters.
     */
    query?: Query;
    /**
     * Default HTTP headers.
     */
    headers?: Record<string, string>;
}

/**
 * An object representing an HTTP request.
 */
export interface IHttpClientRequest<T> {
    /**
     * The URL to make the request to.
     */
    url: string;
    /**
     * The HTTP method of the request.
     */
    method: HTTPMethod;
    /**
     * Request body.
     */
    body?: T;
    /**
     * Query string parameters.
     */
    query?: Query;
    /**
     * HTTP headers.
     */
    headers?: Record<string, string>;
    /**
     * Time to wait between request retries.
     */
    backoff?: number;
    /**
     * number of times to retry a failed request
     */
    retries?: number;
    /**
     * flag to overwrite http to https
     */
    overwriteHttps?: boolean;
    /**
     * flag to deal response with status 400 as 200
     */
    statusCode400As200?: boolean;
    /**
     * flag to deal response with status 401 as 200
     */
    statusCode401As200?: boolean;
    /**
     * flag to deal response with status 422 as 200
     */
    statusCode422As200?: boolean;
    /**
     * flag to deal response with status 304 as 200
     */
    statusCode304As200?: boolean;

    /**
     * flag to deal response with status 429 as 200
     */
     statusCode429As200?: boolean;
    /**
     * flag to deal response with status 402 as 200
     */
    statusCode402As200?: boolean;
    /**
     * flag to deal response with status 500 as 200
     */
    statusCode500As200?: boolean;
    /**
     * flag to deal response with status 501 as 200
     */
    statusCode501As200?: boolean;
    /**
     * response type
     */
    responseType: "arraybuffer" | "blob" | "document" | "json" | "text" | "";
}

/**
 * The response of an HTTP request.
 */
export interface IHttpClientResponse<T> {
    /**
     * HTTP status code.
     */
    status: number;
    /**
     * Response body.
     */
    body?: T;
    /**
     * MIME type of the response.
     */
    type?: string;
    /**
     * HTTP headers.
     */
    headers?: Headers;
}

export const DEFAULT_RETRIES = 3;
export const DEFAULT_BACKOFF = 500;

/**
 * A base class to create different implementations of an HttpClient.
 */
export abstract class HttpClient {
    /**
     * The base URL.
     */
    public readonly url: URL;
    /**
     * Common HTTP headers.
     */
    public readonly headers: Headers;

    /**
     * Create an client instance.
     *
     * @param options Client options.
     */
    constructor(options?: IHttpClientOptions) {
        this.url = new URL(options?.url as string);

        if (options?.query) {
            // prettier-ignore
            this.url.search.merge(
                new URLSearch(options.query)
            );
        }

        if (options?.headers) {
            this.headers = new Headers(options.headers);
        } else {
            this.headers = new Headers();
        }
    }

    /**
     * Send an HTTP request.
     * If the request specifies a retries property the request will be retried if an error ocurrs.
     *
     * @param request Request to make.
     * @returns The HTTP response.
     */
    public async request<T extends string | FormData | Record<string, unknown>>(
        request: IHttpClientRequest<T>
    ): Promise<IHttpClientResponse<Body>> {
        const url = this.url.join(request.url);
        if(request.overwriteHttps){
            url.protocol = 'https';
        }
        const headers = this.headers.clone();
        const { backoff = DEFAULT_BACKOFF } = request;
        const { retries = DEFAULT_RETRIES } = request;
        if (request.headers) {
            headers.merge(new Headers(request.headers));
        }
        if (request.query) {
            url.search.merge(new URLSearch(request.query));
        }
        try {
            const response = await this.send({
                ...request,
                url,
                body: request.body,
                headers
            });
            return response;
        } catch (e) {
            if (retries > 0) {
                await this.delay(backoff);
                return this.request({ ...request, retries: retries - 1 });
            }
            throw e;
        }
    }

    private delay(timeout: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, timeout));
    }

    /**
     * Send an HTTP request to it's corresponding client.
     *
     * @param request Send an HTTP request.
     */
    protected abstract send<T extends string | FormData | Record<string, unknown>>(request: IRequestOptions<T>): Promise<IHttpClientResponse<Body>>;
}



// WEBPACK FOOTER //
// ./src/packages/http/lib/HttpClient.ts